import { useEffect, useState, useCallback } from "react";
import { visionURL } from "../config/environment";
import Jsona from "jsona";
import JsonApiService from "../services/jsonapiService";
import { useFetch } from "./utils";
import visionCallService from "../services/visionCallService";

const dataFormatter = new Jsona();

const positionApiService = async () =>
  new JsonApiService("positions", {
    baseUrl: `${visionURL}/admin_console/api/administration`,
    credentials: "include"
  });

export const usePositions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const getPositions = useCallback(
    async ({ page, sortBy, filters = {} } = {}) => {
      setIsLoading(true);
      let jsonApiFilters = filters;
      if (filters.name_cont && filters.name) {
        jsonApiFilters = (({ name, name_cont, ...props }) => props)(filters);
        jsonApiFilters.name_cont_all = [filters.name, filters.name_cont];
      } else if (!filters.name_cont && filters.name) {
        jsonApiFilters = (({ name, ...props }) => props)(filters);
        jsonApiFilters.name_cont = filters.name;
      }
      const service = await positionApiService();
      const response = await service
        .page(page)
        .sort(sortBy)
        .where(jsonApiFilters)
        .all()
        .then(response => response.json());
      setIsLoading(false);
      return {
        data: dataFormatter.deserialize(response),
        ...response.meta
      };
    },
    [positionApiService, dataFormatter]
  );

  return { getPositions, isLoading };
};

const positionService = async (filter = {}, sort = "") =>
  new JsonApiService("positions", {
    baseUrl: `${visionURL}/admin_console/api/administration`,
    credentials: "include",
    filter: filter,
    sort: sort
  });

export const usePositionsReport = () => {
  const apiUrl = `${visionURL}/admin_console/api/administration/positions_report_path`;
  const report_response = useFetch(apiUrl);
  const report_path = report_response ? report_response["report_path"] : false;

  const report = () => {
    const reportUrl = `${visionURL}${report_path}`;
    var win = window.open(reportUrl, "_blank");
    win.focus();
  };
  return report_path ? report : report_path;
};

export const usePosition = (positionId, includes) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [position, setPosition] = useState(null);

  const updatePosition = async newPositionData => {
    setIsUpdating(true);
    const service = await positionService();
    const response = await service
      .include(includes)
      .update(
        positionId,
        visionCallService.serializeRequestBody(newPositionData, "position")
      )
      .then(response => response.json());
    setIsUpdating(false);
    setPosition(dataFormatter.deserialize(response));
    return response;
  };

  useEffect(() => {
    const fetchPosition = async () => {
      if (positionId) {
        setIsLoading(true);
        const service = await positionService();
        const response = await service
          .include(includes)
          .find(positionId)
          .then(response => response.json());
        setIsLoading(false);
        setPosition(dataFormatter.deserialize(response));
      }
    };
    fetchPosition();
  }, [positionId]);

  return { updatePosition, position, isLoading, isUpdating };
};

export const usePositionAverages = (positionId, trainingObjectType) => {
  const [isLoading, setIsLoading] = useState(false);
  const [positionAverages, setPositionAverages] = useState(null);

  useEffect(() => {
    const fetchPositionAverages = async () => {
      if (positionId) {
        setIsLoading(true);
        const service = await positionService();
        const response = await service
          .where({ training_object_type: trainingObjectType })
          .related(positionId, "averages")
          .then(response => response.json());
        setIsLoading(false);
        setPositionAverages(response);
      }
    };
    fetchPositionAverages();
  }, [positionId, trainingObjectType]);

  return {
    positionAverages,
    isLoading
  };
};

export const usePositionEmployees = ({ position }) => {
  const [loading, setLoading] = useState(false);
  const fetchEmployees = async ({ page, size, sortBy }) => {
    setLoading(true);
    const service = await positionService();
    const response = await service
      .page({ number: page, size })
      .sort(sortBy)
      .include("location")
      .related(position, "employees")
      .then(response => response.json());

    setLoading(false);
    const employees = dataFormatter.deserialize(response);
    return { data: employees, ...response.meta };
  };

  return {
    fetchEmployees,
    loading
  };
};
